import type { Property } from '~/core/entities/property';
import type {
	OrderBy,
	ShortsFilters,
	ShortsSortBy
} from '~/core/entities/property/filters';

import {
	PropertyTypeEnum,
	PropertyTypeSlug,
	propertyTypesSlugByValue,
	propertyTypesValueBySlug
} from '~/core/entities/CRMEnums';
import { normalizeShortsReferenceProperty } from '~/core/entities/property/featuredVideosQueryParams';
import {
	ORDER_BY,
	SHORTS_FEATURED_VIDEOS_INCLUDE_IMAGES_EVERY,
	SHORTS_SORT_BY
} from '~/core/entities/property/filters';
import { getPropertyTypeGroup } from '~/core/entities/property/propertyTypeGroup';

type ListingQueryValue = string | null | (string | null)[] | undefined;
export type ShortsListingQuery = Record<string, ListingQueryValue>;

export const SHORTS_LISTING_QUERY_KEYS = [
	'referenceProperty',
	'displayInHomeShorts',
	'sortBy',
	'orderBy',
	'onlyUnwatched',
	'excludeProperties',
	'regions',
	'cities',
	'states',
	'macroregions',
	'propertyTypes',
	'propertyTypeGroup',
	'radius',
	'minAskingPrice',
	'maxAskingPrice',
	'minArea',
	'maxArea',
	'bedrooms',
	'suites',
	'parkingSpots',
	'poiLocationLat',
	'poiLocationLng'
] as const satisfies readonly (keyof ShortsFilters)[];

const SHORTS_LISTING_CONTEXT_DETECTION_QUERY_KEYS = [
	'referenceProperty',
	'displayInHomeShorts',
	'poiLocationLat',
	'poiLocationLng',
	'propertyTypeGroup',
	'radius',
	'excludeProperties',
	'sortBy',
	'orderBy',
	'onlyUnwatched'
] as const satisfies readonly (keyof ShortsFilters)[];

const SHORTS_APPLIED_FILTER_QUERY_KEYS = [
	'regions',
	'cities',
	'states',
	'propertyTypes',
	'minAskingPrice',
	'maxAskingPrice',
	'minArea',
	'maxArea',
	'suites',
	'bedrooms',
	'parkingSpots',
	'radius'
] as const satisfies readonly (keyof ShortsFilters)[];

export const SHORTS_ROUTE_SYNC_PRESERVED_LISTING_QUERY_KEYS = [
	'referenceProperty',
	'displayInHomeShorts',
	'poiLocationLat',
	'poiLocationLng',
	'excludeProperties',
	'states',
	'sortBy',
	'orderBy',
	'onlyUnwatched',
	'suites',
	'parkingSpots',
	'propertyTypeGroup',
	'radius'
] as const satisfies readonly (keyof ShortsFilters)[];

/**
 * Home widget / context rules before the featured-videos API (not the `/shorts` route).
 * - Active UI filters: sort by success_probability.
 * - No filters + PDP cookie: referenceProperty + priority.
 * - No filters + no cookie: displayInHomeShorts + success_probability.
 */
export function applyHomeShortsRule(
	baseFilters: ShortsFilters,
	hasUserAppliedFilters: boolean,
	lastViewedPropertyId: string | undefined
): ShortsFilters {
	if (hasUserAppliedFilters) {
		return {
			...baseFilters,
			sortBy: SHORTS_SORT_BY.SUCCESS_PROBABILITY,
			orderBy: ORDER_BY.DESC
		};
	}
	if (lastViewedPropertyId) {
		return withSimilarRecommendationParams(baseFilters, lastViewedPropertyId);
	}
	return {
		...baseFilters,
		displayInHomeShorts: true,
		sortBy: SHORTS_SORT_BY.SUCCESS_PROBABILITY,
		orderBy: ORDER_BY.DESC
	};
}

/** Raio fixo (km) da regra de expansão por bairro, confirmado com o back. */
export const SRP_VARIANT_RADIUS_KM = 2.5;

export function withSrpVariantRecommendationParams(
	baseFilters: ShortsFilters
): ShortsFilters {
	const { orderBy: _orderBy, ...filters } = baseFilters;

	const propertyTypeGroup = [
		...new Set(
			(filters.propertyTypes ?? [])
				.map(propertyType =>
					getPropertyTypeGroup(propertyType as PropertyTypeEnum)
				)
				.filter((group): group is string => group !== undefined)
		)
	];

	return {
		...filters,
		...(propertyTypeGroup.length ? { propertyTypeGroup } : {}),
		radius: SRP_VARIANT_RADIUS_KM,
		sortBy: SHORTS_SORT_BY.PRIORITY
	};
}

export function withSimilarRecommendationParams(
	baseFilters: ShortsFilters,
	referenceProperty?: string | null
): ShortsFilters {
	const {
		referenceProperty: _referenceProperty,
		displayInHomeShorts: _displayInHomeShorts,
		...filters
	} = baseFilters;

	const normalizedReferenceProperty = normalizeShortsReferenceProperty(
		referenceProperty ?? undefined
	);

	if (!normalizedReferenceProperty) return filters;

	return sanitizeReferenceRecommendationFilters({
		...filters,
		referenceProperty: normalizedReferenceProperty,
		sortBy: SHORTS_SORT_BY.PRIORITY
	});
}

export function sanitizeReferenceRecommendationFilters(
	filters: ShortsFilters
): ShortsFilters {
	const referenceProperty = normalizeShortsReferenceProperty(
		filters.referenceProperty
	);

	if (!referenceProperty) return filters;

	return {
		...(filters.page !== undefined ? { page: filters.page } : {}),
		...(filters.perPage !== undefined ? { perPage: filters.perPage } : {}),
		referenceProperty,
		...(filters.orderBy !== undefined ? { orderBy: filters.orderBy } : {}),
		...(filters.sortBy !== undefined ? { sortBy: filters.sortBy } : {}),
		...(filters.onlyUnwatched !== undefined
			? { onlyUnwatched: filters.onlyUnwatched }
			: {}),
		...(filters.includeImagesEvery !== undefined
			? { includeImagesEvery: filters.includeImagesEvery }
			: {})
	};
}

/**
 * `/shorts` grid route: keep backend priority for anchored recommendations;
 * otherwise use the existing success-probability ordering.
 */
export function withListingSortRule(filters: ShortsFilters): ShortsFilters {
	if (hasRecommendationReference(filters)) {
		return withSimilarRecommendationParams(filters, filters.referenceProperty);
	}

	return {
		...filters,
		sortBy: SHORTS_SORT_BY.SUCCESS_PROBABILITY,
		orderBy: ORDER_BY.DESC
	};
}

/**
 * Recommendation mode is identified by `referenceProperty`.
 * Some surfaces can fallback to curated home shorts when that recommendation comes back empty.
 */
export function hasRecommendationReference(filters: ShortsFilters): boolean {
	return Boolean(
		normalizeShortsReferenceProperty(filters.referenceProperty) &&
		!filters.displayInHomeShorts
	);
}

export function withDisplayInHomeShortsFallback(
	filters: ShortsFilters
): ShortsFilters {
	return {
		...filters,
		displayInHomeShorts: true,
		referenceProperty: undefined,
		sortBy: SHORTS_SORT_BY.SUCCESS_PROBABILITY,
		orderBy: ORDER_BY.DESC
	};
}

/**
 * `includeImagesEvery` on featured-videos only applies outside the curated home feed
 * (`displayInHomeShorts`).
 */
export function withImagesEvery(filters: ShortsFilters): ShortsFilters {
	if (filters.displayInHomeShorts) {
		return filters;
	}
	return {
		...filters,
		includeImagesEvery: SHORTS_FEATURED_VIDEOS_INCLUDE_IMAGES_EVERY
	};
}

/**
 * `/shorts` grid: always send `includeImagesEvery`, including with `displayInHomeShorts`,
 * to interleave image-only cards. The helper above skips that case (e.g. home widget).
 */
export function withImagesEveryForced(filters: ShortsFilters): ShortsFilters {
	return {
		...filters,
		includeImagesEvery: SHORTS_FEATURED_VIDEOS_INCLUDE_IMAGES_EVERY
	};
}

export type EntryRecommendationProperty = Pick<Property, 'commercialId'>;

/**
 * Direct `/shorts/{commercialId}` entries should default to recommendations
 * anchored on the accessed property instead of the generic home curation.
 */
export function applyShortsEntryRecommendationRule(
	baseFilters: ShortsFilters,
	hasUserAppliedFilters: boolean,
	entryProperty?: EntryRecommendationProperty | null
): ShortsFilters {
	if (hasUserAppliedFilters) {
		return {
			...baseFilters,
			sortBy: SHORTS_SORT_BY.SUCCESS_PROBABILITY,
			orderBy: ORDER_BY.DESC
		};
	}

	return withSimilarRecommendationParams(
		baseFilters,
		entryProperty?.commercialId
	);
}

/**
 * Filtros base do feed de shorts (`/shorts/[commercialId]`): preserva o
 * contexto de origem e intercala imagens pela mesma regra da grade, para que as
 * duas superfícies devolvam a mesma sequência para os mesmos filtros.
 */
export function withFeedBaseFilters(
	filters: ShortsFilters = {}
): ShortsFilters {
	return withImagesEveryForced(filters);
}

/**
 * Serializa um `ShortsFilters` para query params de URL.
 * Usado na navegacao listing -> feed para preservar o contexto da origem.
 * Apenas `includeImagesEvery` e omitido; o feed aplica esse parametro internamente.
 */
export function shortsFiltersToListingQuery(
	filters: ShortsFilters
): Record<string, string> {
	const queryParams: Record<string, string> = {};
	const referenceProperty = normalizeShortsReferenceProperty(
		filters.referenceProperty
	);
	if (referenceProperty) {
		queryParams.referenceProperty = referenceProperty;
		if (filters.sortBy) queryParams.sortBy = filters.sortBy;
		if (filters.orderBy) queryParams.orderBy = filters.orderBy;
		if (filters.onlyUnwatched !== undefined)
			queryParams.onlyUnwatched = String(filters.onlyUnwatched);
		return queryParams;
	}
	if (filters.displayInHomeShorts !== undefined)
		queryParams.displayInHomeShorts = String(filters.displayInHomeShorts);
	if (filters.sortBy) queryParams.sortBy = filters.sortBy;
	if (filters.orderBy) queryParams.orderBy = filters.orderBy;
	if (filters.onlyUnwatched !== undefined)
		queryParams.onlyUnwatched = String(filters.onlyUnwatched);
	if (filters.excludeProperties?.length)
		queryParams.excludeProperties = filters.excludeProperties.join(',');
	if (filters.regions?.length) queryParams.regions = filters.regions.join(',');
	if (filters.cities?.length) queryParams.cities = filters.cities.join(',');
	if (filters.states?.length) queryParams.states = filters.states.join(',');
	if (filters.macroregions?.length)
		queryParams.macroregions = filters.macroregions.join(',');
	if (filters.propertyTypes?.length) {
		const slugs = filters.propertyTypes
			.map(
				propertyType =>
					propertyTypesSlugByValue[propertyType as PropertyTypeEnum]
			)
			.filter(Boolean);
		if (slugs.length) queryParams.propertyTypes = slugs.join(',');
	}
	if (filters.minAskingPrice != null)
		queryParams.minAskingPrice = String(filters.minAskingPrice);
	if (filters.maxAskingPrice != null)
		queryParams.maxAskingPrice = String(filters.maxAskingPrice);
	if (filters.minArea != null) queryParams.minArea = String(filters.minArea);
	if (filters.maxArea != null) queryParams.maxArea = String(filters.maxArea);
	if (filters.bedrooms != null) queryParams.bedrooms = String(filters.bedrooms);
	if (filters.suites != null) queryParams.suites = String(filters.suites);
	if (filters.parkingSpots != null)
		queryParams.parkingSpots = String(filters.parkingSpots);
	if (filters.poiLocationLat != null)
		queryParams.poiLocationLat = String(filters.poiLocationLat);
	if (filters.poiLocationLng != null)
		queryParams.poiLocationLng = String(filters.poiLocationLng);
	if (filters.propertyTypeGroup?.length)
		queryParams.propertyTypeGroup = filters.propertyTypeGroup.join(',');
	if (filters.radius != null) queryParams.radius = String(filters.radius);
	return queryParams;
}

function queryValueToString(value: ListingQueryValue): string | undefined {
	return typeof value === 'string' ? value : undefined;
}

function queryValueToList(value: ListingQueryValue): string[] | undefined {
	const rawValues = Array.isArray(value)
		? value.filter((item): item is string => typeof item === 'string')
		: typeof value === 'string'
			? [value]
			: [];
	const items = rawValues
		.flatMap(rawValue => rawValue.split(','))
		.map(item => item.trim())
		.filter(Boolean);
	return items?.length ? items : undefined;
}

function queryValueToNumber(value: ListingQueryValue): number | undefined {
	const rawValue = queryValueToString(value);
	if (rawValue === undefined) return undefined;
	const parsedNumber = Number(rawValue);
	return Number.isFinite(parsedNumber) ? parsedNumber : undefined;
}

function queryValueToInteger(value: ListingQueryValue): number | undefined {
	const parsedNumber = queryValueToNumber(value);
	if (parsedNumber === undefined) return undefined;
	return Number.isInteger(parsedNumber) ? parsedNumber : undefined;
}

function queryValueToOptionalBool(
	value: ListingQueryValue
): boolean | undefined {
	const rawValue = queryValueToString(value);
	if (rawValue === 'true') return true;
	if (rawValue === 'false') return false;
	return undefined;
}

function queryValueToOrderBy(value: ListingQueryValue): OrderBy | undefined {
	const rawValue = queryValueToString(value);
	if (rawValue === ORDER_BY.ASC || rawValue === ORDER_BY.DESC) return rawValue;
	return undefined;
}

function queryValueToShortsSortBy(
	value: ListingQueryValue
): ShortsSortBy | undefined {
	const rawValue = queryValueToString(value);
	if (
		rawValue === SHORTS_SORT_BY.ASKING_PRICE ||
		rawValue === SHORTS_SORT_BY.SUCCESS_PROBABILITY ||
		rawValue === SHORTS_SORT_BY.PRIORITY ||
		rawValue === SHORTS_SORT_BY.RELEVANCE
	) {
		return rawValue;
	}
	return undefined;
}

function queryValueToPropertyTypes(
	value: ListingQueryValue
): PropertyTypeEnum[] | undefined {
	const propertyTypeValues = new Set(Object.values(PropertyTypeEnum));
	const propertyTypes = queryValueToList(value)
		?.map(
			slug =>
				propertyTypesValueBySlug[slug as PropertyTypeSlug] ??
				(propertyTypeValues.has(slug as PropertyTypeEnum)
					? (slug as PropertyTypeEnum)
					: undefined)
		)
		.filter((propertyType): propertyType is PropertyTypeEnum =>
			Boolean(propertyType)
		);
	return propertyTypes?.length ? propertyTypes : undefined;
}

export function shortsListingQueryToFilters(
	query: ShortsListingQuery
): ShortsFilters {
	const referenceProperty = queryValueToString(query.referenceProperty);
	const displayInHomeShorts = queryValueToOptionalBool(
		query.displayInHomeShorts
	);
	const sortBy = queryValueToShortsSortBy(query.sortBy);
	const orderBy = queryValueToOrderBy(query.orderBy);
	const onlyUnwatched = queryValueToOptionalBool(query.onlyUnwatched);
	const excludeProperties = queryValueToList(query.excludeProperties);
	const regions = queryValueToList(query.regions);
	const cities = queryValueToList(query.cities);
	const states = queryValueToList(query.states);
	const macroregions = queryValueToList(query.macroregions);
	const propertyTypes = queryValueToPropertyTypes(query.propertyTypes);
	const minAskingPrice = queryValueToNumber(query.minAskingPrice);
	const maxAskingPrice = queryValueToNumber(query.maxAskingPrice);
	const minArea = queryValueToNumber(query.minArea);
	const maxArea = queryValueToNumber(query.maxArea);
	const bedrooms = queryValueToInteger(query.bedrooms);
	const suites = queryValueToInteger(query.suites);
	const parkingSpots = queryValueToInteger(query.parkingSpots);
	const poiLocationLat = queryValueToNumber(query.poiLocationLat);
	const poiLocationLng = queryValueToNumber(query.poiLocationLng);
	const propertyTypeGroup = queryValueToList(query.propertyTypeGroup);
	const radius = queryValueToNumber(query.radius);

	return {
		...(referenceProperty ? { referenceProperty } : {}),
		...(displayInHomeShorts !== undefined ? { displayInHomeShorts } : {}),
		...(sortBy ? { sortBy } : {}),
		...(orderBy ? { orderBy } : {}),
		...(onlyUnwatched !== undefined ? { onlyUnwatched } : {}),
		...(excludeProperties ? { excludeProperties } : {}),
		...(regions ? { regions } : {}),
		...(cities ? { cities } : {}),
		...(states ? { states } : {}),
		...(macroregions ? { macroregions } : {}),
		...(propertyTypes ? { propertyTypes } : {}),
		...(minAskingPrice !== undefined ? { minAskingPrice } : {}),
		...(maxAskingPrice !== undefined ? { maxAskingPrice } : {}),
		...(minArea !== undefined ? { minArea } : {}),
		...(maxArea !== undefined ? { maxArea } : {}),
		...(bedrooms !== undefined ? { bedrooms } : {}),
		...(suites !== undefined ? { suites } : {}),
		...(parkingSpots !== undefined ? { parkingSpots } : {}),
		...(poiLocationLat !== undefined && poiLocationLng !== undefined
			? { poiLocationLat, poiLocationLng }
			: {}),
		...(propertyTypeGroup ? { propertyTypeGroup } : {}),
		...(radius !== undefined ? { radius } : {})
	};
}

export function hasShortsListingQueryContext(
	query: ShortsListingQuery
): boolean {
	const filters = shortsListingQueryToFilters(query);
	return SHORTS_LISTING_CONTEXT_DETECTION_QUERY_KEYS.some(
		key => filters[key] !== undefined
	);
}

/**
 * Detecta se o usuário aplicou algum filtro de busca real na URL do feed
 * (não conta `referenceProperty`, ordenação ou paginação — ver PP-2263).
 */
export function hasShortsAppliedFilters(query: ShortsListingQuery): boolean {
	const filters = shortsListingQueryToFilters(query);
	return (
		SHORTS_APPLIED_FILTER_QUERY_KEYS.some(key => filters[key] !== undefined) ||
		queryValueToList(query.features) !== undefined
	);
}
